import { Radio, RadioGroup, Toggle } from 'rsuite';
import './UserPermissionsPicker.css';

// The same expansion the server makes, driven by the catalog it sends.
const expand = (byKey, keys) => {
  const out = new Set();
  const visit = (key) => {
    if (out.has(key) || !byKey.has(key)) return;
    out.add(key);
    byKey.get(key).implies.forEach(visit);
  };
  keys.forEach(visit);
  return out.has('admin') ? new Set(byKey.keys()) : out;
};

function Row({ label, description, children }) {
  return (
    <div className="perm-row">
      <div className="perm-row__text">
        <div className="perm-row__label">{label}</div>
        {description ? <div className="perm-row__desc" title={description}>{description}</div> : null}
      </div>
      {children}
    </div>
  );
}

/**
 * One user's stored permission keys. Ladders (levels that build on each other)
 * are one choice each; every other permission is a switch in its group, ticked
 * and locked when something else already grants it. Administrator locks the lot.
 */
export default function UserPermissionsPicker({ catalog, ladders = [], value, onChange, canGrantAdmin, disabled }) {
  const byKey = new Map(catalog.map((p) => [p.key, p]));
  const effective = expand(byKey, value);
  const isAdmin = value.includes('admin');
  const locked = disabled || isAdmin;
  const laddered = new Set(ladders.flatMap((ladder) => ladder.keys));
  const groups = [...new Set(catalog.map((p) => p.group).filter(Boolean))];
  const implied = (key) => expand(byKey, value.filter((k) => k !== key)).has(key);

  const setLevel = (ladder, key) =>
    onChange([...value.filter((k) => !ladder.keys.includes(k)), ...(key ? [key] : [])]);
  const toggle = (key, on) => onChange(on ? [...value, key] : value.filter((k) => k !== key));
  const levelOf = (ladder) => [...ladder.keys].reverse().find((key) => effective.has(key)) || '';

  const admin = byKey.get('admin');

  return (
    <div className="perm-picker">
      {canGrantAdmin && admin ? (
        <div className="perm-group perm-group--admin">
          <Row label={admin.label} description={admin.description}>
            <Toggle size="sm" checked={isAdmin} disabled={disabled} onChange={(on) => toggle('admin', on)} />
          </Row>
        </div>
      ) : null}

      {/* Administrator grants everything, so the rest folds away while it is on. */}
      <div className={`ppc-collapse${isAdmin ? '' : ' is-open'}`} inert={isAdmin}>
        <div className="ppc-collapse__inner perm-picker__groups">
          {groups.map((group) => (
            <div key={group} className="perm-group">
              <div className="perm-group__title">{group}</div>

              {ladders.filter((ladder) => ladder.group === group).map((ladder) => (
                <Row key={ladder.id} label={ladder.label}>
                  <RadioGroup
                    inline
                    appearance="picker"
                    size="sm"
                    value={levelOf(ladder)}
                    onChange={(key) => setLevel(ladder, key)}
                    disabled={locked}
                    aria-label={ladder.label}
                  >
                    <Radio value="">{ladder.none}</Radio>
                    {ladder.keys.map((key) => (
                      <Radio key={key} value={key}>{byKey.get(key)?.short || byKey.get(key)?.label}</Radio>
                    ))}
                  </RadioGroup>
                </Row>
              ))}

              {catalog.filter((p) => p.group === group && !laddered.has(p.key)).map((p) => (
                <Row key={p.key} label={p.label} description={p.description}>
                  <Toggle
                    size="sm"
                    checked={effective.has(p.key)}
                    disabled={locked || implied(p.key)}
                    onChange={(on) => toggle(p.key, on)}
                  />
                </Row>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
